import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/adminApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, DollarSign, Image, Settings2, AlertTriangle } from 'lucide-react';
import type {
  ImageApiType,
  ImageModelSpec,
  ImageModelSpecInput,
  MissingPricingModel,
} from '@/types/admin';

/* ─────────────────────────── shared types ─────────────────────────── */

interface ModelPricing {
  id: string;
  provider: string;
  model: string;
  inputCost: number | null;
  outputCost: number | null;
  cachedInputCost: number | null;
  cacheWriteCost: number | null;
  createdAt: string;
  updatedAt: string;
}

interface ImageModelPricing {
  id: string;
  provider: string;
  model: string;
  quality: string;
  size: string;
  costUsd: number;
  createdAt: string;
  updatedAt: string;
}

interface LLMModel {
  id: string;
  name: string;
  providerId: string;
  isActive: boolean;
  provider?: { id: string; name: string };
}

/* ──────────── Missing-pricing banner (cross-tab alert) ──────────── */
//
// Surfaces (provider, model) pairs that have produced AiCostEvent rows
// with `meta.pricingSource = 'missing_pricing'` — i.e. the cost engine
// silently wrote $0 because no ModelPricing row matched. The fix is to
// add a row in the LLM Pricing tab below.

function MissingPricingBanner({
  onDismiss,
}: {
  onDismiss?: () => void;
}) {
  const [models, setModels] = useState<MissingPricingModel[] | null>(null);
  const [totalEvents, setTotalEvents] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 30-day window — long enough to catch a model added mid-month, short
        // enough to drop alerts for models we already added pricing for.
        const data = await adminApi.getMissingPricingModels({ period: 30 });
        if (!cancelled) {
          setModels(data.models);
          setTotalEvents(data.totalAffectedEvents);
        }
      } catch (error) {
        // Banner is informational — log silently and stay hidden on failure.
        console.error('[MissingPricingBanner] load failed', error);
        if (!cancelled) setModels([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading || !models || models.length === 0) return null;

  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-4 mb-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-red-900">
              {models.length} {models.length === 1 ? 'model is' : 'models are'} billing $0
              <span className="font-normal text-red-700">
                {' '}— no <code className="bg-red-100 px-1 rounded text-xs">ModelPricing</code> row matched
              </span>
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="text-xs text-red-700 hover:text-red-900 underline"
              >
                {collapsed ? 'Show' : 'Hide'}
              </button>
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="text-xs text-red-700 hover:text-red-900"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-red-800 mt-1">
            {totalEvents.toLocaleString()} cost {totalEvents === 1 ? 'event' : 'events'} in
            the last 30 days were recorded with <code className="bg-red-100 px-1 rounded text-xs">costUsd=0</code>.
            Add a pricing row below to start billing them correctly.
          </p>
          {!collapsed && (
            <div className="mt-3 overflow-x-auto rounded border border-red-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-red-100/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-red-900">Provider</th>
                    <th className="px-3 py-2 text-left font-medium text-red-900">Model</th>
                    <th className="px-3 py-2 text-right font-medium text-red-900">Events</th>
                    <th className="px-3 py-2 text-right font-medium text-red-900">Tokens</th>
                    <th className="px-3 py-2 text-left font-medium text-red-900">First seen</th>
                    <th className="px-3 py-2 text-left font-medium text-red-900">Last seen</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {models.map(m => (
                    <tr key={`${m.provider}:${m.model}`} className="border-t border-red-100">
                      <td className="px-3 py-2 text-gray-700">{m.provider}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-900">{m.model}</td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {m.eventCount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {m.totalTokens.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(m.firstSeenAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(m.lastSeenAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/admin/cost-events?provider=${encodeURIComponent(m.provider)}&model=${encodeURIComponent(m.model)}&pricingSource=missing_pricing`}
                          className="text-xs text-red-700 hover:text-red-900 underline whitespace-nowrap"
                        >
                          View events →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── LLM Models Tab (existing) ────────────────── */

type BackfillPreview = {
  pricingId: string;
  provider: string;
  model: string;
  candidateCount: number;
  totalTokens: number;
  estimatedCostUsd: number;
  affectedUserCount: number;
};

function LLMModelsTab() {
  const [pricings, setPricings] = useState<ModelPricing[]>([]);
  const [llmModels, setLlmModels] = useState<LLMModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<ModelPricing | null>(null);
  const [formData, setFormData] = useState({ provider: '', model: '', inputCost: '', outputCost: '', cachedInputCost: '', cacheWriteCost: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState<BackfillPreview | null>(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const isEditing = selectedPricing !== null;

  useEffect(() => { fetchPricings(); fetchLLMModels(); }, []);

  const fetchPricings = async () => {
    try {
      setIsLoading(true);
      const data = await adminApi.getModelPricings();
      setPricings(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch model pricings');
    } finally { setIsLoading(false); }
  };

  const fetchLLMModels = async () => {
    try {
      setIsLoadingModels(true);
      const data = await adminApi.getAllLLMModels();
      setLlmModels(data);
    } catch (error) {
      console.error('Failed to fetch LLM models:', error);
    } finally { setIsLoadingModels(false); }
  };

  const filteredPricings = useMemo(() => {
    if (!searchTerm) return pricings;
    const term = searchTerm.toLowerCase();
    return pricings.filter(p => p.model.toLowerCase().includes(term));
  }, [pricings, searchTerm]);

  const availableProviders = useMemo(() => {
    const set = new Set(
      llmModels.map(m => m.provider?.name).filter((n): n is string => !!n)
    );
    return [
      { value: '', label: 'Any provider (fallback for legacy rows)' },
      ...Array.from(set).sort().map(p => ({ value: p, label: p })),
    ];
  }, [llmModels]);

  const availableModels = useMemo(() => {
    const existingKeys = new Set(pricings.map(p => `${p.provider ?? ''}:${p.model}`));
    const inProvider =
      formData.provider === ''
        ? llmModels
        : llmModels.filter(m => m.provider?.name === formData.provider);
    return inProvider
      .filter(m => !existingKeys.has(`${formData.provider}:${m.name}`))
      .map(m => ({
        value: m.name,
        label: m.provider?.name ? `${m.name} (${m.provider.name})` : m.name,
      }));
  }, [llmModels, pricings, formData.provider]);

  const handleCreate = () => { setSelectedPricing(null); setFormData({ provider: '', model: '', inputCost: '', outputCost: '', cachedInputCost: '', cacheWriteCost: '' }); setIsModalOpen(true); };
  const handleEdit = (p: ModelPricing) => { setSelectedPricing(p); setFormData({ provider: p.provider ?? '', model: p.model, inputCost: p.inputCost?.toString() || '', outputCost: p.outputCost?.toString() || '', cachedInputCost: p.cachedInputCost?.toString() || '', cacheWriteCost: p.cacheWriteCost?.toString() || '' }); setIsModalOpen(true); };

  const handleDelete = async (p: ModelPricing) => {
    if (!confirm(`Delete pricing for "${p.model}"?`)) return;
    try { await adminApi.deleteModelPricing(p.id); toast.success('Deleted'); fetchPricings(); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Failed to delete'); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.model.trim()) { toast.error('Model name is required'); return; }
    const inputCost = formData.inputCost.trim() ? parseFloat(formData.inputCost) : null;
    const outputCost = formData.outputCost.trim() ? parseFloat(formData.outputCost) : null;
    const cachedInputCost = formData.cachedInputCost.trim() ? parseFloat(formData.cachedInputCost) : null;
    const cacheWriteCost = formData.cacheWriteCost.trim() ? parseFloat(formData.cacheWriteCost) : null;
    if (inputCost !== null && (isNaN(inputCost) || inputCost < 0)) { toast.error('Input cost must be a valid positive number'); return; }
    if (outputCost !== null && (isNaN(outputCost) || outputCost < 0)) { toast.error('Output cost must be a valid positive number'); return; }
    if (cachedInputCost !== null && (isNaN(cachedInputCost) || cachedInputCost < 0)) { toast.error('Cached input cost must be a valid positive number'); return; }
    if (cacheWriteCost !== null && (isNaN(cacheWriteCost) || cacheWriteCost < 0)) { toast.error('Cache write cost must be a valid positive number'); return; }
    try {
      setIsSaving(true);
      const payload: any = {
        provider: formData.provider.trim(),
        model: formData.model.trim(),
      };
      if (inputCost !== null) payload.inputCost = inputCost;
      if (outputCost !== null) payload.outputCost = outputCost;
      if (cachedInputCost !== null) payload.cachedInputCost = cachedInputCost;
      if (cacheWriteCost !== null) payload.cacheWriteCost = cacheWriteCost;
      if (isEditing && selectedPricing) {
        await adminApi.updateModelPricing(selectedPricing.id, payload);
        toast.success('Updated');
        setIsModalOpen(false);
        fetchPricings();
      } else {
        const created = await adminApi.createModelPricing(payload);
        toast.success('Created');
        setIsModalOpen(false);
        fetchPricings();
        // New pricing rows may match existing missing_pricing events. Offer
        // backfill if any exist.
        if (created?.id) {
          try {
            const preview = await adminApi.previewModelPricingBackfill(created.id);
            if (preview.candidateCount > 0) setBackfillPreview(preview);
          } catch (err) {
            console.error('[ModelPricing] backfill preview failed', err);
          }
        }
      }
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  const handleApplyBackfill = async () => {
    if (!backfillPreview) return;
    try {
      setIsBackfilling(true);
      const result = await adminApi.applyModelPricingBackfill(backfillPreview.pricingId);
      toast.success(
        `Backfilled ${result.updatedCount.toLocaleString()} events ($${result.totalCostApplied.toFixed(4)})`,
      );
      setBackfillPreview(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to backfill');
    } finally {
      setIsBackfilling(false);
    }
  };

  const fmt = (cost: number | null) => cost === null ? 'Not set' : `$${cost.toFixed(4)}`;
  const fmtDate = (d: string) => new Date(d).toLocaleString();

  return (
    <>
      <MissingPricingBanner />
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button onClick={handleCreate} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />Add LLM Pricing
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-left">All LLM Pricings ({filteredPricings.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input type="text" placeholder="Search by model name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : filteredPricings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{searchTerm ? 'No results' : 'No pricings configured'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Input Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cached Input Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cache Write Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPricings.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">{p.provider || <span className="italic text-gray-400">any</span>}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><DollarSign className="h-3 w-3 mr-1 text-green-600 inline" />{fmt(p.inputCost)} / 1M</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><DollarSign className="h-3 w-3 mr-1 text-orange-600 inline" />{fmt(p.cachedInputCost)} / 1M</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><DollarSign className="h-3 w-3 mr-1 text-amber-600 inline" />{fmt(p.cacheWriteCost)} / 1M</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><DollarSign className="h-3 w-3 mr-1 text-blue-600 inline" />{fmt(p.outputCost)} / 1M</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmtDate(p.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmtDate(p.updatedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(p)}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {backfillPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-gray-900">Backfill matching events?</h2>
            <p className="text-sm text-gray-600 mt-2">
              <span className="font-mono">{backfillPreview.provider || 'any'}</span> /{' '}
              <span className="font-mono">{backfillPreview.model}</span> now has pricing.{' '}
              {backfillPreview.candidateCount.toLocaleString()} existing{' '}
              {backfillPreview.candidateCount === 1 ? 'event' : 'events'} were recorded
              at <code className="bg-gray-100 px-1 rounded text-xs">$0</code> because no
              pricing row matched.
            </p>
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex justify-between py-0.5">
                <span className="text-gray-600">Events</span>
                <span className="font-medium text-gray-900">
                  {backfillPreview.candidateCount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-gray-600">Total tokens</span>
                <span className="font-medium text-gray-900">
                  {backfillPreview.totalTokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-gray-600">Estimated cost</span>
                <span className="font-medium text-green-700">
                  ${backfillPreview.estimatedCostUsd.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-gray-600">Affected users</span>
                <span className="font-medium text-gray-900">
                  {backfillPreview.affectedUserCount.toLocaleString()}
                </span>
              </div>
            </div>
            <p className="mt-3 text-xs text-amber-700">
              Recomputed cost flows into monthly and session usage totals — users may
              see their balances move once their cache refreshes.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setBackfillPreview(null)}
                disabled={isBackfilling}
              >
                Skip
              </Button>
              <Button type="button" onClick={handleApplyBackfill} disabled={isBackfilling}>
                {isBackfilling ? 'Backfilling…' : 'Backfill now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-gray-900">{isEditing ? 'Edit' : 'Create'} LLM Pricing</h2>
            <p className="text-sm text-gray-600 mt-1">Costs are per million tokens.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                <Select
                  value={formData.provider}
                  onChange={e => setFormData({ ...formData, provider: e.target.value, model: '' })}
                  options={availableProviders}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pick the provider this pricing applies to. <span className="font-medium">Any provider</span> = fallback for legacy rows; specific provider scopes the rate when the same model is hosted on multiple providers with different rates.
                </p>
              </div>
              <div>
                {isEditing ? (
                  <><label className="block text-sm font-medium text-gray-700 mb-1">Model Name *</label><Input value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })} placeholder="e.g. gpt-4o" required /></>
                ) : (
                  <><label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <Select
                    value={formData.model}
                    onChange={e => setFormData({ ...formData, model: e.target.value })}
                    options={[{ value: '', label: formData.provider ? `Select a model from ${formData.provider}...` : 'Select a model...' }, ...availableModels]}
                    required
                    disabled={availableModels.length === 0}
                  />
                  {isLoadingModels && <p className="text-xs text-gray-500 mt-1">Loading models...</p>}
                  {!isLoadingModels && availableModels.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      {formData.provider
                        ? `No models without pricing in "${formData.provider}". Pick a different provider or delete an existing pricing row first.`
                        : 'No models available. Add an LLM model on the Models page first.'}
                    </p>
                  )}</>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Input Cost ($ per 1M tokens)</label>
                <Input type="number" step="0.0001" min="0" value={formData.inputCost} onChange={e => setFormData({ ...formData, inputCost: e.target.value })} placeholder="e.g. 2.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cached Input Cost ($ per 1M tokens)</label>
                <Input type="number" step="0.0001" min="0" value={formData.cachedInputCost} onChange={e => setFormData({ ...formData, cachedInputCost: e.target.value })} placeholder="e.g. 1.25" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cache Write Cost ($ per 1M tokens)</label>
                <Input type="number" step="0.0001" min="0" value={formData.cacheWriteCost} onChange={e => setFormData({ ...formData, cacheWriteCost: e.target.value })} placeholder="e.g. 3.75" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Cost ($ per 1M tokens)</label>
                <Input type="number" step="0.0001" min="0" value={formData.outputCost} onChange={e => setFormData({ ...formData, outputCost: e.target.value })} placeholder="e.g. 10.0" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</Button>
                <Button type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ────────────────────── Image Models Tab (new) ───────────────────── */

function ImageModelsTab() {
  const [pricings, setPricings] = useState<ImageModelPricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<ImageModelPricing | null>(null);
  const [formData, setFormData] = useState({ provider: 'openai', model: '', quality: '', size: '', costUsd: '' });
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = selectedPricing !== null;

  useEffect(() => { fetchPricings(); }, []);

  const fetchPricings = async () => {
    try {
      setIsLoading(true);
      const data = await adminApi.getImageModelPricings();
      setPricings(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch image model pricings');
    } finally { setIsLoading(false); }
  };

  const filteredPricings = useMemo(() => {
    if (!searchTerm) return pricings;
    const term = searchTerm.toLowerCase();
    return pricings.filter(p =>
      p.model.toLowerCase().includes(term) ||
      p.provider.toLowerCase().includes(term) ||
      p.quality.toLowerCase().includes(term)
    );
  }, [pricings, searchTerm]);

  // Group by provider:model for display
  const groupedPricings = useMemo(() => {
    const groups: Record<string, ImageModelPricing[]> = {};
    for (const p of filteredPricings) {
      const key = `${p.provider}:${p.model}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    return groups;
  }, [filteredPricings]);

  const handleCreate = () => {
    setSelectedPricing(null);
    setFormData({ provider: 'openai', model: '', quality: '', size: '', costUsd: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (p: ImageModelPricing) => {
    setSelectedPricing(p);
    setFormData({ provider: p.provider, model: p.model, quality: p.quality, size: p.size, costUsd: p.costUsd.toString() });
    setIsModalOpen(true);
  };

  const handleDelete = async (p: ImageModelPricing) => {
    if (!confirm(`Delete pricing for ${p.provider}/${p.model} (${p.quality}, ${p.size})?`)) return;
    try { await adminApi.deleteImageModelPricing(p.id); toast.success('Deleted'); fetchPricings(); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Failed to delete'); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.provider.trim() || !formData.model.trim() || !formData.quality.trim() || !formData.size.trim()) {
      toast.error('All fields are required');
      return;
    }
    const costUsd = parseFloat(formData.costUsd);
    if (isNaN(costUsd) || costUsd < 0) { toast.error('Cost must be a valid positive number'); return; }
    try {
      setIsSaving(true);
      const payload = { provider: formData.provider.trim(), model: formData.model.trim(), quality: formData.quality.trim(), size: formData.size.trim(), costUsd };
      if (isEditing && selectedPricing) { await adminApi.updateImageModelPricing(selectedPricing.id, payload); toast.success('Updated'); }
      else { await adminApi.createImageModelPricing(payload); toast.success('Created'); }
      setIsModalOpen(false); fetchPricings();
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button onClick={handleCreate} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />Add Image Pricing
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-left">All Image Model Pricings ({filteredPricings.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input type="text" placeholder="Search by provider, model, or quality..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : Object.keys(groupedPricings).length === 0 ? (
            <div className="text-center py-12 text-gray-500">{searchTerm ? 'No results' : 'No image pricings configured'}</div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedPricings).map(([groupKey, items]) => (
                <div key={groupKey}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <Image className="h-4 w-4 mr-2 text-purple-600" />
                    {groupKey}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost per Image</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map(p => (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                p.quality === 'high' || p.quality === 'hd' ? 'bg-purple-100 text-purple-800' :
                                p.quality === 'medium' || p.quality === 'standard' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {p.quality}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{p.size}</td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                              <DollarSign className="h-3 w-3 mr-1 text-green-600 inline" />{p.costUsd.toFixed(4)}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <Button variant="outline" size="sm" onClick={() => handleEdit(p)}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                                <Button variant="outline" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-gray-900">{isEditing ? 'Edit' : 'Create'} Image Pricing</h2>
            <p className="text-sm text-gray-600 mt-1">Cost per image based on quality and size.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                <Input value={formData.provider} onChange={e => setFormData({ ...formData, provider: e.target.value })} placeholder="e.g. openai" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                <Input value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })} placeholder="e.g. gpt-image-1.5" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quality *</label>
                <Input value={formData.quality} onChange={e => setFormData({ ...formData, quality: e.target.value })} placeholder="e.g. medium, high, low, standard, hd" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Size *</label>
                <Input value={formData.size} onChange={e => setFormData({ ...formData, size: e.target.value })} placeholder="e.g. 1024x1024, 1536x1024" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Image (USD) *</label>
                <Input type="number" step="0.001" min="0" value={formData.costUsd} onChange={e => setFormData({ ...formData, costUsd: e.target.value })} placeholder="e.g. 0.063" required />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</Button>
                <Button type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ────────────────── Image Model Specs Tab (capabilities) ─────────── */

const API_TYPES: { value: ImageApiType; label: string }[] = [
  { value: 'OPENAI_IMAGES_GENERATE', label: 'OpenAI /images/generations' },
  { value: 'GOOGLE_GENERATE_TEXT', label: 'Google generateText (Gemini)' },
  { value: 'OPENROUTER_GENERATE_TEXT', label: 'OpenRouter generateText (image via chat)' },
];

const EMPTY_SPEC_FORM = {
  provider: 'openai',
  model: '',
  apiType: 'OPENAI_IMAGES_GENERATE' as ImageApiType,
  sizes: '',
  qualities: '',
  styles: '',
  aspectRatios: '',
  qualityMap: '',
  defaultSize: '',
  defaultQuality: '',
  defaultAspectRatio: '',
  isActive: true,
};

function parseCsv(value: string): string[] {
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function stringifyCsv(value: string[]): string {
  return value.join(', ');
}

function parseQualityMap(value: string): Record<string, string> | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string') out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function stringifyQualityMap(value: Record<string, string> | null): string {
  return value ? JSON.stringify(value) : '';
}

function ImageModelSpecsTab() {
  const [specs, setSpecs] = useState<ImageModelSpec[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<ImageModelSpec | null>(null);
  const [formData, setFormData] = useState(EMPTY_SPEC_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = selected !== null;

  useEffect(() => { fetchSpecs(); }, []);

  const fetchSpecs = async () => {
    try {
      setIsLoading(true);
      const data = await adminApi.getImageModelSpecs();
      setSpecs(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch image model specs');
    } finally { setIsLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return specs;
    const term = searchTerm.toLowerCase();
    return specs.filter(s =>
      s.model.toLowerCase().includes(term) ||
      s.provider.toLowerCase().includes(term) ||
      s.apiType.toLowerCase().includes(term)
    );
  }, [specs, searchTerm]);

  const handleCreate = () => {
    setSelected(null);
    setFormData(EMPTY_SPEC_FORM);
    setIsModalOpen(true);
  };

  const handleEdit = (s: ImageModelSpec) => {
    setSelected(s);
    setFormData({
      provider: s.provider,
      model: s.model,
      apiType: s.apiType,
      sizes: stringifyCsv(s.sizes),
      qualities: stringifyCsv(s.qualities),
      styles: stringifyCsv(s.styles),
      aspectRatios: stringifyCsv(s.aspectRatios),
      qualityMap: stringifyQualityMap(s.qualityMap),
      defaultSize: s.defaultSize ?? '',
      defaultQuality: s.defaultQuality ?? '',
      defaultAspectRatio: s.defaultAspectRatio ?? '',
      isActive: s.isActive,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (s: ImageModelSpec) => {
    if (!confirm(`Delete spec for ${s.provider}/${s.model}?`)) return;
    try {
      await adminApi.deleteImageModelSpec(s.id);
      toast.success('Deleted');
      fetchSpecs();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete');
    }
  };

  const handleToggleActive = async (s: ImageModelSpec) => {
    try {
      await adminApi.updateImageModelSpec(s.id, { isActive: !s.isActive });
      fetchSpecs();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.provider.trim() || !formData.model.trim()) {
      toast.error('Provider and model are required');
      return;
    }
    if (formData.qualityMap.trim() && parseQualityMap(formData.qualityMap) === null) {
      toast.error('Quality map must be valid JSON like {"standard":"medium"}');
      return;
    }

    const payload: ImageModelSpecInput = {
      provider: formData.provider.trim(),
      model: formData.model.trim(),
      apiType: formData.apiType,
      sizes: parseCsv(formData.sizes),
      qualities: parseCsv(formData.qualities),
      styles: parseCsv(formData.styles),
      aspectRatios: parseCsv(formData.aspectRatios),
      qualityMap: parseQualityMap(formData.qualityMap),
      defaultSize: formData.defaultSize.trim() || null,
      defaultQuality: formData.defaultQuality.trim() || null,
      defaultAspectRatio: formData.defaultAspectRatio.trim() || null,
      isActive: formData.isActive,
    };

    try {
      setIsSaving(true);
      if (isEditing && selected) {
        await adminApi.updateImageModelSpec(selected.id, payload);
        toast.success('Updated');
      } else {
        await adminApi.createImageModelSpec(payload);
        toast.success('Created');
      }
      setIsModalOpen(false);
      fetchSpecs();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button onClick={handleCreate} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />Add Image Model Spec
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-left">Image Model Specs ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input type="text" placeholder="Search by provider, model, or API type..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{searchTerm ? 'No results' : 'No image model specs configured'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sizes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qualities</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Styles</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aspect Ratios</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Defaults</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{s.provider}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">{s.model}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">{s.apiType}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">{s.sizes.join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">{s.qualities.join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">{s.styles.join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">{s.aspectRatios.join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        {[s.defaultSize, s.defaultQuality, s.defaultAspectRatio].filter(Boolean).join(' / ') || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(s)}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            s.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {s.isActive ? 'active' : 'inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(s)}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(s)} className="text-red-600 hover:text-red-700"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900">{isEditing ? 'Edit' : 'Create'} Image Model Spec</h2>
            <p className="text-sm text-gray-600 mt-1">Define capabilities the LLM is allowed to select for this model.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                  <Input value={formData.provider} onChange={e => setFormData({ ...formData, provider: e.target.value })} placeholder="e.g. openai" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <Input value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })} placeholder="e.g. gpt-image-2-2026-04-21" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Type *</label>
                <Select
                  value={formData.apiType}
                  onChange={e => setFormData({ ...formData, apiType: e.target.value as ImageApiType })}
                  options={API_TYPES.map(t => ({ value: t.value, label: t.label }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sizes (comma-separated)</label>
                <Input value={formData.sizes} onChange={e => setFormData({ ...formData, sizes: e.target.value })} placeholder="1024x1024, 1536x1024, 1024x1536" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualities (LLM-visible, comma-separated)</label>
                <Input value={formData.qualities} onChange={e => setFormData({ ...formData, qualities: e.target.value })} placeholder="standard, hd" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Styles (comma-separated)</label>
                <Input value={formData.styles} onChange={e => setFormData({ ...formData, styles: e.target.value })} placeholder="vivid, natural" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aspect Ratios (comma-separated)</label>
                <Input value={formData.aspectRatios} onChange={e => setFormData({ ...formData, aspectRatios: e.target.value })} placeholder="1:1, 16:9, 9:16" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quality Map (JSON, LLM-quality → provider-quality)</label>
                <Input value={formData.qualityMap} onChange={e => setFormData({ ...formData, qualityMap: e.target.value })} placeholder='{"standard":"medium","hd":"high"}' />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Size</label>
                  <Input value={formData.defaultSize} onChange={e => setFormData({ ...formData, defaultSize: e.target.value })} placeholder="1024x1024" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Quality</label>
                  <Input value={formData.defaultQuality} onChange={e => setFormData({ ...formData, defaultQuality: e.target.value })} placeholder="standard" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Aspect Ratio</label>
                  <Input value={formData.defaultAspectRatio} onChange={e => setFormData({ ...formData, defaultAspectRatio: e.target.value })} placeholder="1:1" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</Button>
                <Button type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ──────────────────────── Main Page with Tabs ────────────────────── */

export default function AdminModelPricingPage() {
  const [activeTab, setActiveTab] = useState<'llm' | 'image' | 'specs'>('llm');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Model Pricing</h1>
        <p className="text-gray-600 mt-1">Manage pricing for LLM and image generation models</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('llm')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'llm'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DollarSign className="h-4 w-4 mr-2 inline" />
            LLM Models
          </button>
          <button
            onClick={() => setActiveTab('image')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'image'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Image className="h-4 w-4 mr-2 inline" />
            Image Pricing
          </button>
          <button
            onClick={() => setActiveTab('specs')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'specs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings2 className="h-4 w-4 mr-2 inline" />
            Image Model Specs
          </button>
        </nav>
      </div>

      {activeTab === 'llm' && <LLMModelsTab />}
      {activeTab === 'image' && <ImageModelsTab />}
      {activeTab === 'specs' && <ImageModelSpecsTab />}
    </div>
  );
}
